import { formatAud, splitGstInclusive } from "./types";

/** Singapore MEP category used by the DIY library. */
export const SG_MEP_CATEGORY_ID = "cat_mep_sg";

/**
 * Live SGD Stripe Price IDs on the Australian sole-trader account.
 * Tax exclusive. Same face numbers as the AUD catalogue.
 */
export const SGD_DIY_STRIPE_PRICE_IDS = {
  res_mep_acmv: "price_1U6jqGE9AmwLbWLZldSLKkej",
  res_mep_mv: "price_1U6jqHE9AmwLbWLZH5fi8Zj7",
  res_ashrae_l2: "price_1U6jqIE9AmwLbWLZtG8Es6dI",
  res_mep_gm: "price_1U6jqKE9AmwLbWLZts4lUTtA",
  res_ashrae_l3: "price_1U6jqLE9AmwLbWLZycEImlV8",
} as const;

export type SgdDiyResourceId = keyof typeof SGD_DIY_STRIPE_PRICE_IDS;

export function sgdStripePriceIdForResource(resourceId: string): string | undefined {
  return SGD_DIY_STRIPE_PRICE_IDS[resourceId as SgdDiyResourceId];
}

export function isSgdDiyResourceId(resourceId: string | null | undefined): boolean {
  return !!resourceId && resourceId in SGD_DIY_STRIPE_PRICE_IDS;
}

/** Paid Singapore DIY: the five minted SKUs, or anything in cat_mep_sg. */
export function isSingaporeMepDiy(resource: {
  id?: string | null;
  categoryId?: string | null;
  category_id?: string | null;
}): boolean {
  if (isSgdDiyResourceId(resource.id)) return true;
  return (resource.categoryId ?? resource.category_id) === SG_MEP_CATEGORY_ID;
}

export type ResourceCheckoutQuote = {
  currency: "sgd" | "aud";
  stripePriceId?: string;
  subtotalCents: number;
  gstCents: number;
  totalCents: number;
};

/** SGD map: face price, no GST. Everything else: AUD, price_cents is GST-inclusive. */
export function resourceCheckoutQuote(resource: {
  id: string;
  price_cents: number;
}): ResourceCheckoutQuote {
  const stripePriceId = sgdStripePriceIdForResource(resource.id);
  if (stripePriceId) {
    return {
      currency: "sgd",
      stripePriceId,
      subtotalCents: resource.price_cents,
      gstCents: 0,
      totalCents: resource.price_cents,
    };
  }
  const money = splitGstInclusive(resource.price_cents);
  return {
    currency: "aud",
    stripePriceId: undefined,
    subtotalCents: money.subtotalCents,
    gstCents: money.gstCents,
    totalCents: money.totalCents,
  };
}

/** Catalogue display: S$49 / S$59, not SGD 49.00. */
export function formatSgd(cents: number): string {
  const major = cents / 100;
  const formatted = new Intl.NumberFormat("en-SG", {
    minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(major);
  return `S$${formatted}`;
}

export function formatOrderMoney(cents: number, currency: string): string {
  return currency.toLowerCase() === "sgd" ? formatSgd(cents) : formatAud(cents);
}

export function formatPaidSpend(
  orders: Array<{ status: string; currency: string; totalCents: number }>
): string {
  const totals = new Map<string, number>();
  for (const order of orders) {
    if (order.status !== "paid") continue;
    const currency = (order.currency || "aud").toLowerCase();
    totals.set(currency, (totals.get(currency) ?? 0) + order.totalCents);
  }
  if (totals.size === 0) return formatAud(0);
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, cents]) => formatOrderMoney(cents, currency))
    .join(" · ");
}

export function resourceListTaxNote(resource: {
  id?: string | null;
  categoryId?: string | null;
  category_id?: string | null;
  isFree: boolean;
}): string | null {
  if (resource.isFree) return null;
  return isSingaporeMepDiy(resource) ? "tax exclusive" : "incl. GST";
}

export function resourceDetailTaxNote(resource: {
  id?: string | null;
  categoryId?: string | null;
  category_id?: string | null;
  isFree: boolean;
}): string | null {
  if (resource.isFree) return null;
  return isSingaporeMepDiy(resource) ? "tax exclusive" : "incl. GST";
}

export function resourcePriceAmount(resource: {
  id?: string | null;
  categoryId?: string | null;
  category_id?: string | null;
  isFree: boolean;
  priceCents: number;
}): string {
  if (resource.isFree) return "Free";
  return isSingaporeMepDiy(resource) ? formatSgd(resource.priceCents) : formatAud(resource.priceCents);
}

export function orderReceiptAmountLine(order: {
  currency: string;
  totalCents?: number;
  total_cents?: number;
  gstCents?: number;
  gst_cents?: number;
}): string {
  const currency = order.currency || "aud";
  const total = order.totalCents ?? order.total_cents ?? 0;
  const gst = order.gstCents ?? order.gst_cents ?? 0;
  if (currency.toLowerCase() === "sgd") {
    return `We received your payment of ${formatSgd(total)} (tax exclusive).`;
  }
  return `We received your payment of ${formatAud(total)} (incl. GST ${formatAud(gst)}).`;
}
